import type {
    AdvisorDetailClient,
    AdvisorDetailKpis,
    AdvisorDetailResult,
    ClientDetailKpis,
    ClientDetailResult,
    DetailCashflowPoint,
    DetailDestination,
    DetailInstallment,
    DetailItinerary,
} from '@/integrations/supabase/rpc-types';

/**
 * Normalizzazione dei payload di get_client_detail / get_advisor_detail.
 *
 * Le schede leggono `data.stats.kpis.x` in profondita', e un solo livello mancante manda
 * in crash l'intero pannello (ErrorBoundary). Succede in due casi reali:
 *  - il database e' indietro rispetto al frontend: prima della 20260911110000 la RPC
 *    rispondeva piatta (`kpis`, `cashflow`, `destinations` al primo livello, senza `stats`);
 *  - il database omette sezioni o campi a chi non ha diritto di vederli.
 *
 * Qui si fa una volta sola, nel service: la UI e l'export ricevono sempre la forma
 * completa, con 0 per i numeri e [] per le liste che non sono arrivate.
 */

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const rec = (value: unknown): Raw => (isRecord(value) ? value : {});

const num = (value: unknown): number => {
    const n = typeof value === 'string' ? Number(value) : value;
    return typeof n === 'number' && Number.isFinite(n) ? n : 0;
};

const numOrNull = (value: unknown): number | null =>
    value === null || value === undefined ? null : num(value);

const str = (value: unknown, fallback = ''): string =>
    typeof value === 'string' ? value : fallback;

const strOrNull = (value: unknown): string | null =>
    typeof value === 'string' ? value : null;

const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** `stats` se c'e', altrimenti la radice: e' la forma piatta della RPC precedente. */
const statsSection = (raw: Raw): Raw => (isRecord(raw.stats) ? raw.stats : raw);

const normalizeCashflow = (value: unknown): DetailCashflowPoint[] =>
    arr(value).filter(isRecord).map((p) => ({
        month: str(p.month),
        paid: num(p.paid),
        expected: num(p.expected),
    }));

const normalizeDestinations = (value: unknown): DetailDestination[] =>
    arr(value).filter(isRecord).map((d) => ({
        destination: str(d.destination),
        count: num(d.count),
    }));

const normalizeInstallment = (i: Raw): DetailInstallment => ({
    installment_id: str(i.installment_id),
    seq: num(i.seq),
    label: strOrNull(i.label),
    amount: num(i.amount),
    due_date: str(i.due_date),
    status: (str(i.status, 'pending') as DetailInstallment['status']),
    paid_at: strOrNull(i.paid_at),
    paid_amount: numOrNull(i.paid_amount),
    payment_method: strOrNull(i.payment_method),
    is_overdue: i.is_overdue === true,
    platform_fee_due: num(i.platform_fee_due),
});

const normalizeItineraries = (value: unknown): DetailItinerary[] =>
    arr(value).filter(isRecord).map((it) => ({
        itinerary_id: str(it.itinerary_id),
        title: str(it.title),
        status: str(it.status),
        start_date: strOrNull(it.start_date),
        end_date: strOrNull(it.end_date),
        total: num(it.total),
        completed_at: strOrNull(it.completed_at),
        created_at: strOrNull(it.created_at),
        advisor_id: strOrNull(it.advisor_id),
        advisor_name: strOrNull(it.advisor_name),
        client_key: strOrNull(it.client_key),
        client_name: strOrNull(it.client_name),
        destinations: arr(it.destinations).filter((d): d is string => typeof d === 'string'),
        currency: str(it.currency, 'EUR'),
        has_plan: it.has_plan === true,
        installments_count: num(it.installments_count),
        plan_total: num(it.plan_total),
        paid_amount: num(it.paid_amount),
        remaining_amount: num(it.remaining_amount),
        overdue_amount: num(it.overdue_amount),
        platform_fee_due_amount: num(it.platform_fee_due_amount),
        installments: arr(it.installments).filter(isRecord).map(normalizeInstallment),
    }));

const normalizeMeta = (value: unknown): ClientDetailResult['meta'] => {
    const m = rec(value);
    return {
        period: str(m.period, '30d') as ClientDetailResult['meta']['period'],
        period_only: m.period_only === true,
        period_days: num(m.period_days),
        generated_on: str(m.generated_on),
    };
};

/** `null` resta `null`: e' il "non trovato / non visibile" della RPC. */
export function normalizeClientDetail(value: unknown): ClientDetailResult | null {
    if (!isRecord(value) || !isRecord(value.client)) return null;

    const c = value.client;
    const stats = statsSection(value);
    const k = rec(stats.kpis);
    const kpis: ClientDetailKpis = {
        requests_count: num(k.requests_count),
        itineraries_count: num(k.itineraries_count),
        completed_count: num(k.completed_count),
        total_amount: num(k.total_amount),
        paid_amount: num(k.paid_amount),
        remaining_amount: num(k.remaining_amount),
        overdue_amount: num(k.overdue_amount),
        closed_value_total: num(k.closed_value_total),
        platform_fee_due_amount: num(k.platform_fee_due_amount),
    };

    return {
        client: {
            client_key: str(c.client_key),
            client_user_id: strOrNull(c.client_user_id),
            first_name: strOrNull(c.first_name),
            last_name: strOrNull(c.last_name),
            email: strOrNull(c.email),
            phone: strOrNull(c.phone),
            avatar_url: strOrNull(c.avatar_url),
            has_account: c.has_account === true,
            first_seen_at: strOrNull(c.first_seen_at),
            last_seen_at: strOrNull(c.last_seen_at),
        },
        meta: normalizeMeta(value.meta),
        stats: {
            kpis,
            cashflow: normalizeCashflow(stats.cashflow),
            destinations: normalizeDestinations(stats.destinations),
        },
        itineraries: normalizeItineraries(value.itineraries),
    };
}

export function normalizeAdvisorDetail(value: unknown): AdvisorDetailResult | null {
    if (!isRecord(value) || !isRecord(value.advisor)) return null;

    const a = value.advisor;
    const stats = statsSection(value);
    const k = rec(stats.kpis);
    const kpis: AdvisorDetailKpis = {
        requests_count: num(k.requests_count),
        clients_count: num(k.clients_count),
        itineraries_count: num(k.itineraries_count),
        active_count: num(k.active_count),
        completed_count: num(k.completed_count),
        total_amount: num(k.total_amount),
        paid_amount: num(k.paid_amount),
        remaining_amount: num(k.remaining_amount),
        overdue_amount: num(k.overdue_amount),
        closed_value_total: num(k.closed_value_total),
        platform_fee_due_amount: num(k.platform_fee_due_amount),
    };

    const clients: AdvisorDetailClient[] = arr(stats.clients).filter(isRecord).map((cl) => ({
        client_key: str(cl.client_key),
        client_name: strOrNull(cl.client_name),
        email: strOrNull(cl.email),
        requests_count: num(cl.requests_count),
        closed_value: num(cl.closed_value),
    }));

    return {
        advisor: {
            advisor_id: str(a.advisor_id),
            first_name: strOrNull(a.first_name),
            last_name: strOrNull(a.last_name),
            email: strOrNull(a.email),
            avatar_url: strOrNull(a.avatar_url),
            role: str(a.role),
            is_self: a.is_self === true,
        },
        meta: normalizeMeta(value.meta),
        stats: {
            kpis,
            cashflow: normalizeCashflow(stats.cashflow),
            destinations: normalizeDestinations(stats.destinations),
            clients,
        },
        itineraries: normalizeItineraries(value.itineraries),
        // Solo dalla sezione dedicata: il `kpis.margin_total` della forma piatta arrivava
        // a chiunque, e non va ripescato qui.
        ...(isRecord(value.admin) ? { admin: { margin_total: num(value.admin.margin_total) } } : {}),
    };
}
